import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link, UserCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface LinkAgentModalProps {
  children: React.ReactNode;
  onLinked: () => void;
}

export const LinkAgentModal: React.FC<LinkAgentModalProps> = ({ children, onLinked }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const { toast } = useToast();

  const linkAgent = async () => {
    if (!code.trim()) {
      toast({
        title: "Error",
        description: "Please enter an invite code.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('link-athlete', {
        body: { code: code.trim().toUpperCase() }
      });
      
      if (error) throw error;
      
      if (data.success) {
        toast({
          title: "Successfully linked!",
          description: `You are now linked to agent: ${data.agent_name}`,
        });
        setOpen(false);
        setCode('');
        onLinked();
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error('Error linking agent:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to link agent. Please check the code and try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setCode('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="h-5 w-5" />
            Link to Agent
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Enter the invite code provided by your agent to link your account.
          </p>
          
          <div className="space-y-2">
            <Label htmlFor="invite-code">Invite Code</Label>
            <Input
              id="invite-code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="Enter 8-character code"
              className="font-mono text-lg tracking-wider text-center"
              maxLength={8}
            />
          </div>
          
          <div className="bg-muted p-3 rounded-lg">
            <p className="text-sm text-muted-foreground">
              Your agent will provide you with an 8-character code. Once linked, your agent will be able to view your holdings and performance data.
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button 
              onClick={linkAgent} 
              disabled={loading || !code.trim()}
              className="flex-1"
            >
              {loading ? "Linking..." : "Link Agent"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};